/**
 * Konva Stage Initialization and Management
 */
import Konva from "konva";

export class StageManager {
	constructor($stageContainer) {
		this.$stageContainer = $stageContainer;
		this.containerWidth = $stageContainer.width();
		this.containerHeight = $stageContainer.height();

		// Fallback if width is 0 (e.g. hidden or not rendered yet)
		if (!this.containerWidth) {
			this.containerWidth = $stageContainer.parent().width() || 600;
		}

		// Fix height if not set
		if (!this.containerHeight || this.containerHeight === 0) {
			this.containerHeight = this.containerWidth;
			$stageContainer.css("height", this.containerHeight + "px");
		}

		this.stage = null;
		this.bgLayer = null;
		this.mainLayer = null;
		this.transformer = null;

		this.init();
	}

	init() {
		// Create main stage
		this.stage = new Konva.Stage({
			container: "mc-stage-container",
			width: this.containerWidth,
			height: this.containerHeight,
		});

		// Create layers
		this.bgLayer = new Konva.Layer(); // monument background
		this.mainLayer = new Konva.Layer(); // texts, photos, decors

		this.stage.add(this.bgLayer);
		this.stage.add(this.mainLayer);

		// Create main transformer
		this.transformer = new Konva.Transformer({
			rotateEnabled: true,
			rotateAnchorOffset: 20, // Reduced from default 50
			enabledAnchors: ["middle-left", "middle-right"],
			boundBoxFunc: function (oldBox, newBox) {
				if (newBox.width < 40) return oldBox;
				return newBox;
			},
		});

		this.mainLayer.add(this.transformer);
	}

	getStage() {
		return this.stage;
	}

	getBgLayer() {
		return this.bgLayer;
	}

	getMainLayer() {
		return this.mainLayer;
	}

	getTransformer() {
		return this.transformer;
	}

	clearPhotoTransformers(except) {
		const transformers = this.mainLayer.find("Transformer");
		transformers.forEach((tr) => {
			if (tr === this.transformer || tr === except) return;
			tr.nodes([]);
		});
	}

	setPhotosNotActive() {
		const photoTransformers = this.mainLayer.find("Transformer");
		photoTransformers.forEach((tr) => {
			if (tr !== this.transformer) {
				tr.nodes([]);
			}
		});
	}

	clearAllSelections() {
		this.transformer.nodes([]);
		this.clearPhotoTransformers();
		this.mainLayer.draw();
	}

	/**
	 * Serialize the current stage state
	 */
	serializeState(currentProductId, customProductTitle = "") {
		const textsData = [];
		const photosData = [];
		const decorsData = [];

		const textNodes = this.stage.find(".mc-text");
		const decorNodes = this.stage.find(".mc-decor");
		const photoGroups = this.stage.find(".mc-photo-group");

		textNodes.forEach((node) => {
			const colorId = node.getAttr("mcColorId") || "";
			const colorLabel = node.getAttr("mcColorLabel") || "";
			const colorFill = node.getAttr("mcColorFill") || node.fill();
			const colorOpacity =
				node.getAttr("mcColorOpacity") ?? node.opacity();
			textsData.push({
				id: node.id(),
				value: node.text(),
				x: node.x(),
				y: node.y(),
				rotation: node.rotation(),
				fontFamily: node.fontFamily(),
				fontSize: node.fontSize(),
				align: node.align(),
				width: node.width(),
				padding: node.padding(),
				colorId,
				colorLabel,
				colorFill,
				colorOpacity,
			});
		});

		photoGroups.forEach((group, index) => {
			photosData.push({
				id: index,
				x: group.x(),
				y: group.y(),
				width: group.width(),
				height: group.height(),
				scaleX: group.scaleX(),
				scaleY: group.scaleY(),
				rotation: group.rotation(),
				meta: window.mcPhotoState[index] || null,
			});
		});

		decorNodes.forEach((node) => {
			decorsData.push({
				title: node.getAttr("decorTitle") || "",
				x: node.x(),
				y: node.y(),
				scaleX: node.scaleX(),
				scaleY: node.scaleY(),
				rotation: node.rotation(),
			});
		});

		const state = {
			product_id: currentProductId,
			texts: textsData,
			photos: photosData,
			decors: decorsData,
		};

		if (customProductTitle) {
			state.product_title = customProductTitle;
		}

		return state;
	}

	/**
	 * Get preview PNG of the stage
	 */
	getPreviewPNG() {
		return this.stage.toDataURL({ pixelRatio: 2 });
	}
}
